import { DEFAULT_TRAVEL, NEEDS, NEED_KEYS, type NeedKey, type TravelOption } from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { number } from './form';

const pounds = (pence: number) => (pence / 100).toFixed(2);

// Travel: the ways players get about (walking, the bus, the DLR, the tube, a cab). Each has its name and icon,
// its fare, how long the journey takes (the journey is shown on the map for that long) and what it does to the
// player's needs. A way can be closed for a while. Changes are part of the content draft, published with it.
export function TravelPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const travel = working?.travel ?? DEFAULT_TRAVEL;
  // content made before travel was content gets the starting list the first time it is changed
  const change = (mode: TravelOption['mode'], fn: (draft: TravelOption) => void) =>
    editor.edit((content) => {
      content.travel ??= structuredClone(DEFAULT_TRAVEL);
      const option = content.travel.find((item) => item.mode === mode);
      if (option) fn(option);
    });
  return (
    <section>
      <PageHeader
        title="Travel"
        description="How players get about London: the fare, how long the journey takes (players see it on the map), what it does to their needs, and whether it is running. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          {travel.map((option) => {
            const key = (name: string) => `${option.mode}-${name}`;
            return (
              <fieldset
                key={option.mode}
                className="admin-panel content-section"
                disabled={readOnly}
                aria-label={option.label}
              >
                <h2 className="content-subtitle">
                  <span aria-hidden="true">{option.icon}</span> {option.label}
                  <span className="admin-muted"> · {option.mode}</span>
                </h2>
                <div className="content-grid">
                  <div className="admin-field">
                    <Label htmlFor={key('label')}>Name</Label>
                    <Input
                      id={key('label')}
                      value={option.label}
                      maxLength={20}
                      onChange={(e) => change(option.mode, (item) => void (item.label = e.target.value))}
                    />
                  </div>
                  <div className="admin-field">
                    <Label htmlFor={key('icon')}>Icon</Label>
                    <Input
                      id={key('icon')}
                      value={option.icon}
                      maxLength={16}
                      onChange={(e) => change(option.mode, (item) => void (item.icon = e.target.value))}
                    />
                  </div>
                  <div className="admin-field">
                    <Label htmlFor={key('fare')}>Fare (£)</Label>
                    <Input
                      id={key('fare')}
                      type="number"
                      step="0.01"
                      min={0}
                      value={pounds(option.farePence)}
                      onChange={(e) =>
                        change(
                          option.mode,
                          (item) => void (item.farePence = Math.max(0, Math.round(number(e.target.value, 0) * 100))),
                        )
                      }
                    />
                  </div>
                  <div className="admin-field">
                    <Label htmlFor={key('seconds')}>Journey (seconds)</Label>
                    <Input
                      id={key('seconds')}
                      type="number"
                      min={3}
                      max={600}
                      value={option.seconds}
                      onChange={(e) =>
                        change(
                          option.mode,
                          (item) =>
                            void (item.seconds = Math.max(
                              3,
                              Math.min(600, Math.round(number(e.target.value, item.seconds))),
                            )),
                        )
                      }
                    />
                  </div>
                </div>
                <label className="designer-check">
                  <Switch
                    checked={option.visible}
                    aria-label={`${option.label} is running`}
                    onCheckedChange={(on) => change(option.mode, (item) => void (item.visible = on))}
                  />{' '}
                  Running (players can choose it)
                </label>
                <p className="content-mini-title">What the journey does to needs (−100 to 100)</p>
                <div className="content-needs">
                  {NEED_KEYS.map((need: NeedKey) => (
                    <label key={need}>
                      <span>
                        {NEEDS[need].icon} {NEEDS[need].label}
                      </span>
                      <Input
                        type="number"
                        min={-100}
                        max={100}
                        value={option.effects[need] ?? ''}
                        onChange={(e) =>
                          change(option.mode, (item) => {
                            const value = Math.round(number(e.target.value, 0));
                            if (value) item.effects[need] = Math.max(-100, Math.min(100, value));
                            else delete item.effects[need];
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
      )}
    </section>
  );
}
